// src/components/PlatformSelector.js - Cleaned with extracted hooks
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./css-styles/index.css";
import { API_BASE_URL } from '../config';
import logger from '../services/logger';

// Import extracted hooks
import { useSessionCleanup } from '../hooks/useSessionCleanup';
import { usePlatformManagement } from '../hooks/usePlatformManagement';
import { useGuestTimer } from '../hooks/useGuestTimer';
import { useEnhancedUserBubble } from '../hooks/useEnhancedUserBubble';

// Import pure component
import { EnhancedUserBubble } from './EnhancedUserBubble';

const PlatformSelection = () => {
  const starsContainerRef = useRef(null);
  const particlesContainerRef = useRef(null);
  const navigate = useNavigate();
  
  // Use extracted hooks for all logic
  const { 
    handleGuestSessionExpired,
    createEnhancedMongoLogout 
  } = useSessionCleanup();
  
  const {
    platformStates,
    showConnectionSuccess,
    connectedPlatform,
    handlePlatformLogout,
    handleSpotifyAction,
    // PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
    // handleYouTubeAction,
    handleForceLogin,
    getStatusText,
    getActionButtonText,
    checkPlatformStates
  } = usePlatformManagement();
  
  const {
    isGuest,
    guestTimeRemaining,
    formatTime
  } = useGuestTimer(navigate, handleGuestSessionExpired);
  
  const {
    user
  } = useEnhancedUserBubble(navigate, 'platforms');

  // Create the enhanced MongoDB logout function
  const handleMongoLogout = createEnhancedMongoLogout(user, platformStates, navigate);

  // Legacy logout function (kept for backward compatibility)
  const handleLogout = async () => {
    try {
      // Clear platform sessions via Flask server
      await fetch(`${API_BASE_URL}/logout`, { method: 'GET', credentials: 'include' });
      
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('guestSession');
      
      // Redirect to auth page
      navigate('/');
    } catch (error) {
      logger.error('Error during logout:', error);
      // Force redirect anyway
      navigate('/');
    }
  };

  return (
    <div className="platform-selection-page">
      <div className="page-background"></div>
      <div ref={starsContainerRef} className="stars"></div>
      <div ref={particlesContainerRef} className="particles"></div>

      {/* OAuth Success Notification */}
      {showConnectionSuccess && connectedPlatform && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(29, 185, 84, 0.9)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          border: '1px solid rgba(29, 185, 84, 0.3)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            ✅ {connectedPlatform.charAt(0).toUpperCase() + connectedPlatform.slice(1)} Connected!
          </div>
          <div style={{ fontSize: '14px' }}>
            You can now access the dashboard with {connectedPlatform}
          </div>
        </div>
      )}

      {/* Enhanced User Bubble */}
      {(user || isGuest) && (
        <EnhancedUserBubble
          navigate={navigate}
          formatTime={formatTime}
          guestTimeRemaining={guestTimeRemaining}
          platformStates={platformStates}
          onPlatformLogout={handlePlatformLogout}
          onMongoLogout={handleMongoLogout}
          showBackToPlatforms={false}
          currentPage="platforms"
          handleSpotifyAction={handleSpotifyAction}
          // PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
          // handleYouTubeAction={handleYouTubeAction}
        />
      )}

      {/* Main Platform Selection Content */}
      <div className="platform-content">
        <div className="platform-header">
          {/* BETA: Spotify available now, more platforms coming soon */}
          <h1>
            Connect Your Music Platform
            <span style={{
              marginLeft: '12px',
              padding: '4px 12px',
              fontSize: '14px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.2), rgba(29, 185, 84, 0.2))',
              border: '1px solid rgba(0, 217, 255, 0.4)',
              borderRadius: '20px',
              color: '#00D9FF',
              verticalAlign: 'middle'
            }}>BETA</span>
          </h1>
          <p>Spotify Premium available now - Apple Music coming soon</p>
          {isGuest && (
            <div style={{
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              margin: '16px 0',
              color: '#ffa500'
            }}>
              <strong>⏱️ Guest Mode:</strong> You have {formatTime(guestTimeRemaining)} remaining. 
              Limited features available. <button 
                onClick={() => navigate('/')}
                style={{
                  background: 'transparent',
                  color: '#ffa500',
                  border: '1px solid #ffa500',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  marginLeft: '8px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Create Account
              </button>
            </div>
          )}
        </div>

        <div className="platforms-grid">
          {/* Spotify Card */}
          <div className={`platform-card spotify ${
            platformStates.spotify.active ? 'active' : ''}`}>
            <div className="platform-logo spotify">♪</div>
            <h3>Spotify</h3>
            <p>
              Connect to Spotify for access to millions of songs, playlists, 
              and personalized recommendations with intelligent AI curation.
            </p>
            
            <div className={`connection-status ${platformStates.spotify.status}`}>
              {getStatusText('spotify')}
            </div>
            
            {/* Main Action Button */}
            <button 
              className={`platform-action-button ${platformStates.spotify.connected ? 'active' : 'connect'}`}
              onClick={() => handleSpotifyAction(navigate)}
            >
              {getActionButtonText('spotify')}
            </button>

            {/* Account Management Buttons */}
            {platformStates.spotify.connected && (
              <div className="account-management-buttons" style={{
                display: 'flex',
                gap: '8px',
                marginTop: '12px',
                justifyContent: 'center'
              }}>
                <button 
                  className="secondary-action-button"
                  onClick={() => handlePlatformLogout('spotify')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #666',
                    color: '#ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
                <button 
                  className="secondary-action-button"
                  onClick={() => handleForceLogin('spotify')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #1db954',
                    color: '#1db954',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Switch Account
                </button>
              </div>
            )}
          </div>

          {/* PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music) */}
          {/* YouTube Music Card */}
          {/* <div className={`platform-card youtube ${
            platformStates.youtube.active ? 'active' : ''}`}>
            <div className="platform-logo youtube">▶</div>
            <h3>YouTube Music</h3>
            <p>
              Connect to YouTube Music for unlimited access to songs, albums,
              and playlists with intelligent AI curation.
            </p>

            <div className={`connection-status ${platformStates.youtube.status}`}>
              {getStatusText('youtube')}
            </div>

            {/* Main Action Button */}
            {/* <button
              className={`platform-action-button ${platformStates.youtube.connected ? 'active' : 'connect'}`}
              onClick={() => handleYouTubeAction(navigate)}
            >
              {getActionButtonText('youtube')}
            </button> */}

            {/* Account Management Buttons */}
            {/* {platformStates.youtube.connected && (
              <div className="account-management-buttons" style={{
                display: 'flex',
                gap: '8px',
                marginTop: '12px',
                justifyContent: 'center'
              }}>
                <button
                  className="secondary-action-button"
                  onClick={() => handlePlatformLogout('youtube')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #666',
                    color: '#ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
                <button
                  className="secondary-action-button"
                  onClick={() => handleForceLogin('youtube')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #ff0000',
                    color: '#ff0000',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Switch Account
                </button>
              </div>
            )} */}
          {/* </div> */}
        </div>

        {/* Footer */}
        <div className="platform-footer">
          {isGuest ? (
            <>
              <p>Limited guest experience - missing personalization features</p>
              <div className="secondary-actions">
                <button 
                  className="secondary-action-button"
                  onClick={() => navigate('/')}
                >
                  Create Free Account
                </button>
              </div>
            </>
          ) : (
            <>
              {/* BETA: Spotify available now, more platforms coming soon */}
              <p>Spotify Premium required for full AI DJ experience - More platforms launching soon</p>
              <div className="secondary-actions">
                <button 
                  className="secondary-action-button"
                  onClick={() => checkPlatformStates()}
                >
                  Refresh Status
                </button>
                <button 
                  className="secondary-action-button"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default PlatformSelection;